import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { toast } from 'sonner';

import {
  X, Trophy, XCircle, RotateCcw, Trash2, DollarSign,
  Calendar, TrendingUp, Edit2, Check, Clock, UserPlus, Globe, MoreHorizontal,
  ThumbsUp, ThumbsDown, Building2, User, Mail, Phone, Briefcase, Tag,
  ExternalLink, Copy, Search, ArrowLeftRight, CheckCircle2
} from 'lucide-react';
import type { Deal, Stage } from './types';
import { ActivityTimeline } from './ActivityTimeline';
import { WonLostDialog } from './WonLostDialog';
import { formatDealValue } from './DealCard';
import { useAuth } from '@/context/AuthContext';
import { leadService } from '@/lib/leadService';

interface DealDetailDialogProps {
  deal: Deal | null;
  stages: Stage[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (dealId: string, updates: any) => Promise<void>;
  onMarkWon: (dealId: string, reason?: string) => Promise<void>;
  onMarkLost: (dealId: string, reason: string) => Promise<void>;
  onReopen: (dealId: string, stageId: string) => Promise<void>;
  onDelete: (dealId: string) => Promise<void>;
  onConvertToClient?: (dealId: string) => void;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-slate-400' },
  { value: 'medium', label: 'Medium', color: 'text-blue-400' },
  { value: 'high', label: 'High', color: 'text-orange-400' },
  { value: 'critical', label: 'Critical', color: 'text-red-400' },
];

function EditableField({
  label, value, onSave, type = 'text', placeholder
}: {
  label: string; value: string; onSave: (v: string) => void;
  type?: string; placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          type={type}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          className="h-8 text-sm bg-background border-input flex-1"
          onKeyDown={e => {
            if (e.key === 'Enter') { onSave(draft); setEditing(false); }
            if (e.key === 'Escape') { setDraft(value); setEditing(false); }
          }}
        />
        <button onClick={() => { onSave(draft); setEditing(false); }}
          className="text-workspace-accent hover:text-workspace-accent-hover">
          <Check className="w-4 h-4" />
        </button>
        <button onClick={() => { setDraft(value); setEditing(false); }}
          className="text-muted-foreground hover:text-foreground">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="w-full text-left group flex items-center justify-between gap-1.5 px-3 py-2 rounded-md border border-input bg-background shadow-sm hover:border-ring transition-all cursor-text min-h-[36px]"
    >
      <span className={`text-sm truncate ${value ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
        {value || placeholder || `Set ${label.toLowerCase()}`}
      </span>
      <Edit2 className="w-3.5 h-3.5 text-muted-foreground/0 group-hover:text-muted-foreground/50 transition-colors shrink-0" />
    </div>
  );
}

export function DealStagesBar({ deal, stages, onUpdate, onMarkWon, onMarkLost, onReopen }: any) {
  const allSteps = [
    ...stages,
    { id: 'won', name: 'Closed Won', icon: Trophy, isFixed: true },
    { id: 'lost', name: 'Closed Lost', icon: XCircle, isFixed: true },
  ];

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMoved, setDragMoved] = useState(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const edgeScrollRef = useRef<'left' | 'right' | null>(null);
  const rAFRef = useRef<number | null>(null);

  const startDragging = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragMoved(false);
    startX.current = e.pageX - (scrollContainerRef.current?.offsetLeft || 0);
    scrollLeft.current = scrollContainerRef.current?.scrollLeft || 0;
  };

  const stopDragging = () => {
    setIsDragging(false);
    edgeScrollRef.current = null;
  };

  const onDragAndHover = (e: React.MouseEvent) => {
    if (isDragging) {
      const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0);
      const walk = (x - startX.current) * 2;
      if (Math.abs(walk) > 5) setDragMoved(true);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
      }
    } else {
      if (!scrollContainerRef.current) return;
      const rect = scrollContainerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      
      if (mouseX < 40 && mouseX > 0) edgeScrollRef.current = 'left';
      else if (mouseX > rect.width - 40 && mouseX < rect.width) edgeScrollRef.current = 'right';
      else edgeScrollRef.current = null;
    }
  };

  const onMouseLeave = () => {
    stopDragging();
    edgeScrollRef.current = null;
  };

  useEffect(() => {
    const loop = () => {
      if (edgeScrollRef.current && scrollContainerRef.current && !isDragging) {
        scrollContainerRef.current.scrollLeft += edgeScrollRef.current === 'right' ? 6 : -6;
      }
      rAFRef.current = requestAnimationFrame(loop);
    };
    rAFRef.current = requestAnimationFrame(loop);
    return () => {
      if (rAFRef.current) cancelAnimationFrame(rAFRef.current);
    };
  }, [isDragging]);

  return (
    <div className="w-full bg-muted/10 border-b border-border py-3 px-6 flex items-center justify-between shrink-0">
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-x-auto min-w-0 pr-4 cursor-grab active:cursor-grabbing select-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        onMouseDown={startDragging}
        onMouseUp={stopDragging}
        onMouseLeave={onMouseLeave}
        onMouseMove={onDragAndHover}
      >
        <div className="flex items-center min-w-max w-full">
          {allSteps.map((step, i) => {
            const isWon = step.id === 'won';
            const isLost = step.id === 'lost';
            const isActiveStage = deal.status === 'active' && deal.stage_id === step.id;
            const isActiveWon = deal.status === 'won' && isWon;
            const isActiveLost = deal.status === 'lost' && isLost;
            const isActive = isActiveStage || isActiveWon || isActiveLost;

            const isFirst = i === 0;

            return (
              <div 
                key={step.id}
                className={`relative flex-1 flex items-center justify-center h-[32px] group cursor-pointer ${isFirst ? '' : '-ml-[6px]'}`}
                style={{
                  clipPath: isFirst 
                    ? 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)' 
                    : 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)',
                  zIndex: isActive ? 50 : 40 - i
                }}
                onClick={(e) => {
                  if (dragMoved) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  if (isWon) onMarkWon(deal.id);
                  else if (isLost) onMarkLost(deal.id, 'Lost');
                  else if (deal.status !== 'active' && onReopen) onReopen(deal.id, step.id);
                  else onUpdate(deal.id, { stageId: step.id });
                }}
            >
              {/* Outer border simulated by background */}
              <div className={`absolute inset-0 transition-colors ${isActive ? 'bg-workspace-accent' : 'bg-border group-hover:bg-workspace-accent/50'}`} />
              
              {/* Inner background */}
              <div 
                className={`absolute inset-[1px] transition-colors ${isActive ? 'bg-workspace-accent' : 'bg-card group-hover:bg-muted'}`}
                style={{
                  clipPath: isFirst 
                    ? 'polygon(0 0, calc(100% - 9px) 0, 100% 50%, calc(100% - 9px) 100%, 0 100%)' 
                    : 'polygon(0 0, calc(100% - 9px) 0, 100% 50%, calc(100% - 9px) 100%, 0 100%, 9px 50%)',
                }}
              />

              {/* Content (static, gives width) */}
              <div 
                className={`relative z-10 flex items-center justify-center transition-colors ${isActive ? 'text-white' : 'text-muted-foreground group-hover:text-foreground'}`}
                style={{
                  paddingLeft: isFirst ? '12px' : '20px',
                  paddingRight: '22px'
                }}
              >
                {step.icon && <step.icon className="w-3.5 h-3.5 mr-1.5 shrink-0" />}
                <span className="text-[11px] font-semibold tracking-wide whitespace-nowrap">{step.title || step.name}</span>
              </div>
            </div>
          );
        })}
        </div>
      </div>
      
      {deal.status === 'active' && (
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-full"
            onClick={() => onMarkWon(deal.id)}
          >
            <ThumbsUp className="w-4 h-4" fill="currentColor" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full"
            onClick={() => onMarkLost(deal.id, 'Lost')}
          >
            <ThumbsDown className="w-4 h-4" fill="currentColor" />
          </Button>
        </div>
      )}
    </div>
  );
}

export const DealDetailDialog: React.FC<DealDetailDialogProps> = ({
  deal, stages, open, onOpenChange,
  onUpdate, onMarkWon, onMarkLost, onReopen, onDelete, onConvertToClient
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wonLostDialog, setWonLostDialog] = useState<'won' | 'lost' | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'activity'>('info');
  const [localProbability, setLocalProbability] = useState(deal?.probability || 0);
  const [descEditing, setDescEditing] = useState(false);
  const [descDraft, setDescDraft] = useState(deal?.description || '');

  // Contact & Company change pickers
  const [availableContacts, setAvailableContacts] = useState<any[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [contactTab, setContactTab] = useState<'lead' | 'client'>('lead');
  const [contactSearch, setContactSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  useEffect(() => {
    if (deal) setLocalProbability(deal.probability || 0);
  }, [deal?.probability, deal?.id]);

  useEffect(() => {
    if (deal && !descEditing) setDescDraft(deal.description || '');
  }, [deal?.description, descEditing]);

  // Load available contacts and companies for pickers
  useEffect(() => {
    if (open && deal?.workspace_id) {
      leadService.getLeadsForDealPicker(deal.workspace_id)
        .then(contacts => setAvailableContacts(contacts))
        .catch(err => console.error('Failed to load contacts for deal dialog:', err));

      leadService.getCompaniesForPicker(deal.workspace_id)
        .then(companies => setAvailableCompanies(companies))
        .catch(err => console.error('Failed to load companies for deal dialog:', err));
    }
  }, [open, deal?.workspace_id]);

  if (!deal) return null;

  const activeStages = stages.filter(s => !s.isWon && !s.isLost);

  const handleFieldUpdate = async (field: string, value: any) => {
    await onUpdate(deal.id, { [field]: value });
  };

  const handleContactSelect = async (contact: any) => {
    const newClientInfo = {
      firstName: contact.first_name || '',
      lastName: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      source: contact.source || '',
      designation: contact.designation || '',
      companyName: contact.company_name || '',
      companyCategory: contact.company_category || '',
      website: contact.website || '',
      contactType: contact.type,
    };

    const updates: any = {
      contactId: contact.id,
      companyId: contact.company_id || null,
      source: contact.source || deal.source || '',
      clientInfo: newClientInfo,
    };

    await onUpdate(deal.id, updates);
    setContactPickerOpen(false);
    toast.success(`Linked to ${contact.first_name} ${contact.last_name}`);
  };

  const handleCompanySelect = async (company: any | null) => {
    const currentInfo = deal.clientInfo || {};
    const newClientInfo = {
      ...currentInfo,
      companyName: company ? company.name : '',
      companyCategory: company ? (company.category || '') : '',
      website: company ? (company.website || '') : (currentInfo.website || ''),
    };

    const updates: any = {
      companyId: company ? company.id : null,
      clientInfo: newClientInfo,
    };

    await onUpdate(deal.id, updates);
    setCompanyPickerOpen(false);
    toast.success(company ? `Linked to company: ${company.name}` : 'Removed company link');
  };

  const handleDelete = async () => {
    await onDelete(deal.id);
    onOpenChange(false);
  };

  const copyToClipboard = (text: string, label: string) => {
    if (!text || text === '—') return;
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${label} to clipboard`);
  };

  // Compute field values from deal and clientInfo
  const fullName = [deal.clientInfo?.firstName, deal.clientInfo?.lastName].filter(Boolean).join(' ').trim() || '—';
  const source = deal.source || deal.clientInfo?.source || '—';
  const email = deal.clientInfo?.email || '—';
  const phone = deal.clientInfo?.phone || '—';
  const companyName = deal.clientInfo?.companyName || deal.company?.name || '—';
  const category = deal.clientInfo?.companyCategory || '—';
  const website = deal.clientInfo?.website || '—';
  const designation = deal.clientInfo?.designation || '—';
  const contactType = deal.clientInfo?.contactType || (deal.contactId ? 'contact' : null);

  const filteredContacts = availableContacts
    .filter(c => c.type === contactTab)
    .filter(c => {
      if (!contactSearch.trim()) return true;
      const term = contactSearch.toLowerCase();
      const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      const company = (c.company_name || '').toLowerCase();
      const mail = (c.email || '').toLowerCase();
      return name.includes(term) || company.includes(term) || mail.includes(term);
    });

  const filteredCompanies = availableCompanies.filter(comp => {
    if (!companySearch.trim()) return true;
    const term = companySearch.toLowerCase();
    return (comp.name || '').toLowerCase().includes(term) || (comp.category || '').toLowerCase().includes(term);
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl w-[95vw] bg-background dark:bg-card border-border p-0 gap-0 overflow-hidden h-[88vh] max-h-[92vh] max-md:h-full max-md:max-h-screen flex flex-col">
          <DialogTitle className="sr-only">Deal details</DialogTitle>
          <DialogDescription className="sr-only">Deal details</DialogDescription>

          {/* Header */}
          <div className={`px-6 py-4 border-b border-border flex items-start gap-3 ${
            deal.status === 'won' ? 'bg-emerald-500/5' :
            deal.status === 'lost' ? 'bg-red-500/5' : 'bg-muted/10'
          }`}>
            <div className="flex-1 min-w-0">
              {/* Tabs */}
              <div className="flex items-center gap-6 mt-1">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`text-sm font-semibold pb-1.5 border-b-2 transition-colors ${
                    activeTab === 'info' 
                      ? 'border-workspace-accent text-foreground' 
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Deal Info
                </button>
                <button
                  onClick={() => setActiveTab('activity')}
                  className={`text-sm font-semibold pb-1.5 border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'activity' 
                      ? 'border-workspace-accent text-foreground' 
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Activity
                  {(deal.activities?.length ?? 0) > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-muted text-[10px] font-bold text-muted-foreground">
                      {deal.activities?.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {(deal.status === 'won' || deal.status === 'lost') && activeStages.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReopen(deal.id, activeStages[0].id)}
                  className="h-8 px-3 gap-1 text-xs"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reopen
                </Button>
              )}
              {contactType === 'lead' && (
                <Button
                  size="sm"
                  onClick={() => onConvertToClient?.(deal.id)}
                  className="h-8 px-3 gap-1 text-xs bg-workspace-accent text-white hover:bg-workspace-accent-hover hidden md:flex shadow-sm"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Convert to Client
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    destructive
                    onClick={() => {
                      if (confirm('Delete this deal permanently?')) {
                        handleDelete();
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Deal
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <DealStagesBar 
            deal={deal} 
            stages={activeStages} 
            onUpdate={onUpdate} 
            onMarkWon={() => setWonLostDialog('won')} 
            onMarkLost={() => setWonLostDialog('lost')}
            onReopen={onReopen}
          />

          {/* Body */}
          <div className="flex-1 overflow-hidden flex">
            {activeTab === 'info' ? (
              <>
                {/* Left: Deal Details */}
                <ScrollArea className="flex-1 border-r border-border bg-muted/5">
                  <div className="flex flex-col h-full">
                    {/* Won/Lost reason */}
                    {(deal.wonReason || deal.lostReason) && (
                      <div className="px-6 pt-6 pb-2">
                        {deal.status === 'won' && deal.wonReason && (
                          <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-400 mb-4">
                            🏆 {deal.wonReason}
                          </div>
                        )}
                        {deal.status === 'lost' && deal.lostReason && (
                          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400 mb-4">
                            ❌ {deal.lostReason}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex-1 overflow-hidden flex flex-col">
                      <div>
                        <div className="px-6 py-6">
                          {/* Deal Title */}
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="space-y-1 col-span-2">
                              <Label>Deal Title</Label>
                              <EditableField
                                label="Deal Title"
                                value={deal.title || ''}
                                placeholder="Enter Deal Title"
                                onSave={v => handleFieldUpdate('title', v)}
                              />
                            </div>
                          </div>

                          {/* Fields grid */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Value */}
                            <div>
                              <Label className="flex items-center gap-1.5 mb-1">
                                <DollarSign className="w-3 h-3" /> Deal Value
                              </Label>
                              <EditableField
                                label="Value"
                                value={deal.value ? String(deal.value) : ''}
                                type="number"
                                placeholder="0.00"
                                onSave={v => handleFieldUpdate('value', parseFloat(v) || 0)}
                              />
                              {deal.value > 0 && (
                                <div className="text-xs font-semibold text-emerald-500 mt-1">
                                  = {formatDealValue(deal.value, deal.currency)}
                                </div>
                              )}
                            </div>

                            {/* Probability */}
                            <div>
                              <Label className="flex items-center gap-1.5 mb-1">
                                <TrendingUp className="w-3 h-3" /> Win Probability %
                              </Label>
                              <EditableField
                                label="Probability"
                                value={String(localProbability)}
                                type="number"
                                placeholder="0"
                                onSave={v => handleFieldUpdate('probability', Math.min(100, Math.max(0, parseInt(v) || 0)))}
                              />
                              <Slider
                                className="mt-3"
                                value={[localProbability]}
                                onValueChange={(v) => setLocalProbability(v[0])}
                                onValueCommit={(v) => handleFieldUpdate('probability', v[0])}
                                max={100}
                                step={1}
                              />
                            </div>

                            {/* Priority */}
                            <div>
                              <Label className="mb-1 block">Priority</Label>
                              <Select value={deal.priority} onValueChange={v => handleFieldUpdate('priority', v)}>
                                <SelectTrigger className="h-9 text-sm bg-background border-input">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PRIORITY_OPTIONS.map(p => (
                                    <SelectItem key={p.value} value={p.value}>
                                      <span className={p.color}>{p.label}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Expected Close */}
                            <div>
                              <Label className="flex items-center gap-1.5 mb-1">
                                <Calendar className="w-3 h-3" /> Expected Close
                              </Label>
                              <Input
                                type="date"
                                value={deal.expectedCloseDate || ''}
                                onChange={e => handleFieldUpdate('expectedCloseDate', e.target.value || null)}
                                className="h-9 text-sm bg-background border-input"
                              />
                            </div>
                          </div>

                          {/* Actual close (for won/lost) */}
                          {deal.actualCloseDate && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-6">
                              <Clock className="w-3.5 h-3.5" />
                              Closed on {new Date(deal.actualCloseDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </div>
                          )}

                          {/* Notes */}
                          <div className="space-y-1 mt-6">
                            <Label>Notes</Label>
                            {descEditing ? (
                              <div className="space-y-2 mt-1">
                                <Textarea
                                  autoFocus
                                  value={descDraft}
                                  onChange={e => setDescDraft(e.target.value)}
                                  placeholder="Add notes..."
                                  className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700 resize-none text-sm text-foreground focus-visible:ring-yellow-400"
                                  rows={4}
                                />
                                <div className="flex justify-end gap-2">
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDescDraft(deal.description); setDescEditing(false); }}>Cancel</Button>
                                  <Button size="sm" className="h-7 text-xs bg-workspace-accent text-white hover:bg-workspace-accent-hover" onClick={() => { handleFieldUpdate('description', descDraft); setDescEditing(false); }}>Save</Button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => { setDescDraft(deal.description); setDescEditing(true); }}
                                className="text-sm text-foreground whitespace-pre-wrap p-3 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-100/50 dark:bg-yellow-900/20 hover:border-yellow-300 dark:hover:border-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 cursor-text min-h-[6rem] transition-colors"
                              >
                                {deal.description || <span className="text-muted-foreground/50 italic flex items-center gap-1.5"><Edit2 className="w-3 h-3" /> Add notes...</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollArea>

                {/* Right: Read-Only Linked Contact & Company Fields */}
                <ScrollArea className="flex-1 shrink-0 bg-muted/5">
                  <div className="w-full p-6 space-y-6">
                    {/* Contact Details Section */}
                    <div className="space-y-3.5">
                      <div className="flex items-center gap-2 pb-1 border-b border-border/50">
                        <User className="w-3.5 h-3.5 text-workspace-accent" />
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                          Contact Details
                        </span>
                      </div>

                      {/* 1. Full Name */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Full Name</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center justify-between gap-2 min-h-[38px]">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <span className={`text-sm truncate font-medium select-text ${fullName !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {fullName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {contactType && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenChange(false);
                                  if (contactType === 'lead') {
                                    navigate(deal.contactId ? `/crm/leads?leadId=${deal.contactId}` : '/crm/leads');
                                  } else {
                                    navigate(deal.contactId ? `/crm/clients?tab=clients&clientId=${deal.contactId}` : '/crm/clients?tab=clients');
                                  }
                                }}
                                className="focus:outline-none transition-transform active:scale-95 group/badge"
                                title={`View ${contactType === 'lead' ? 'Lead' : 'Client'} Details`}
                              >
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] capitalize font-semibold px-2 py-0.5 rounded-full shrink-0 cursor-pointer inline-flex items-center gap-1 transition-all ${
                                    contactType === 'lead'
                                      ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20 group-hover/badge:bg-indigo-500/20 group-hover/badge:border-indigo-500/40'
                                      : 'bg-blue-500/10 text-blue-500 border-blue-500/20 group-hover/badge:bg-blue-500/20 group-hover/badge:border-blue-500/40'
                                  }`}
                                >
                                  <span>{contactType}</span>
                                  <ExternalLink className="w-2.5 h-2.5 opacity-70 group-hover/badge:opacity-100 transition-opacity" />
                                </Badge>
                              </button>
                            )}
                            <Popover
                              open={contactPickerOpen}
                              onOpenChange={(isOpen) => {
                                setContactPickerOpen(isOpen);
                                if (isOpen) setContactSearch('');
                              }}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={`p-1.5 rounded-md transition-colors shrink-0 ${
                                    contactPickerOpen
                                      ? 'text-workspace-accent bg-workspace-accent/15'
                                      : 'text-muted-foreground hover:text-workspace-accent hover:bg-workspace-accent/10'
                                  }`}
                                  title="Switch Client / Lead"
                                >
                                  <ArrowLeftRight className="w-3.5 h-3.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="end"
                                sideOffset={8}
                                className="z-[60] w-[350px] p-0 shadow-2xl border-border bg-card rounded-xl overflow-hidden"
                              >
                                <div className="px-3.5 py-2.5 border-b border-border/60 bg-muted/20 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <User className="w-3.5 h-3.5 text-workspace-accent" />
                                    <span className="text-xs font-semibold text-foreground">Switch Contact</span>
                                  </div>
                                  <span className="text-[11px] text-muted-foreground font-medium">
                                    {availableContacts.length} total
                                  </span>
                                </div>

                                <div className="p-3 space-y-2.5">
                                  <Tabs
                                    value={contactTab}
                                    onValueChange={(v) => {
                                      setContactTab(v as 'lead' | 'client');
                                      setContactSearch('');
                                    }}
                                    className="w-full"
                                  >
                                    <TabsList className="w-full grid grid-cols-2 h-8 p-0.5 bg-muted/50 rounded-lg">
                                      <TabsTrigger
                                        value="lead"
                                        className="text-xs h-7 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs font-medium"
                                      >
                                        Leads ({availableContacts.filter(c => c.type === 'lead').length})
                                      </TabsTrigger>
                                      <TabsTrigger
                                        value="client"
                                        className="text-xs h-7 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs font-medium"
                                      >
                                        Clients ({availableContacts.filter(c => c.type === 'client').length})
                                      </TabsTrigger>
                                    </TabsList>

                                    <div className="relative mt-2.5">
                                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                                      <Input
                                        autoFocus
                                        placeholder={`Search ${contactTab === 'lead' ? 'leads' : 'clients'}...`}
                                        value={contactSearch}
                                        onChange={e => setContactSearch(e.target.value)}
                                        className="pl-8 pr-7 h-8 text-xs bg-background border-border"
                                      />
                                      {contactSearch && (
                                        <button
                                          type="button"
                                          onClick={() => setContactSearch('')}
                                          className="absolute right-2 top-2 p-0.5 text-muted-foreground hover:text-foreground rounded"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>

                                    <ScrollArea className="h-[210px] mt-2 pr-1">
                                      <div className="space-y-1">
                                        {filteredContacts.length > 0 ? (
                                          filteredContacts.map(c => {
                                            const isSelected = deal.contactId === c.id;
                                            const cName = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email;
                                            return (
                                              <div
                                                key={c.id}
                                                onClick={() => handleContactSelect(c)}
                                                className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                                                  isSelected
                                                    ? 'bg-workspace-accent/10 border border-workspace-accent/30 text-foreground'
                                                    : 'hover:bg-muted/60 border border-transparent text-foreground'
                                                }`}
                                              >
                                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
                                                    c.type === 'lead' ? 'bg-indigo-500/10 text-indigo-500' : 'bg-blue-500/10 text-blue-500'
                                                  }`}>
                                                    {c.first_name?.[0]?.toUpperCase() || c.last_name?.[0]?.toUpperCase() || 'U'}
                                                  </div>
                                                  <div className="flex flex-col min-w-0 overflow-hidden">
                                                    <span className="font-medium truncate text-xs">{cName}</span>
                                                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground truncate">
                                                      {c.email && <span className="truncate">{c.email}</span>}
                                                      {c.company_name && <span className="truncate shrink-0">• {c.company_name}</span>}
                                                    </div>
                                                  </div>
                                                </div>
                                                {isSelected && (
                                                  <CheckCircle2 className="w-3.5 h-3.5 text-workspace-accent shrink-0 ml-1.5" />
                                                )}
                                              </div>
                                            );
                                          })
                                        ) : (
                                          <div className="text-center py-8 text-xs text-muted-foreground">
                                            No {contactTab}s found
                                          </div>
                                        )}
                                      </div>
                                    </ScrollArea>
                                  </Tabs>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                      </div>

                      {/* 2. Email */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Email</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center justify-between gap-2 min-h-[38px] group">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <span className={`text-sm truncate font-medium select-text ${email !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {email}
                            </span>
                          </div>
                          {email !== '—' && (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(email, 'Email')}
                              className="text-muted-foreground hover:text-foreground p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Copy Email"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* 3. Phone Number */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Phone Number</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center justify-between gap-2 min-h-[38px] group">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Phone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <span className={`text-sm truncate font-medium select-text ${phone !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {phone}
                            </span>
                          </div>
                          {phone !== '—' && (
                            <button
                              type="button"
                              onClick={() => copyToClipboard(phone, 'Phone')}
                              className="text-muted-foreground hover:text-foreground p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Copy Phone"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* 4. Source */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Source</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center gap-2 min-h-[38px] select-text">
                          <Tag className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className={`text-sm truncate font-medium ${source !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {source}
                          </span>
                        </div>
                      </div>

                      {/* 5. Designation */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Designation</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center gap-2 min-h-[38px] select-text">
                          <Briefcase className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className={`text-sm truncate font-medium ${designation !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {designation}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Company Details Section */}
                    <div className="space-y-3.5 pt-1">
                      <div className="flex items-center gap-2 pb-1 border-b border-border/50">
                        <Building2 className="w-3.5 h-3.5 text-workspace-accent" />
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                          Company Details
                        </span>
                      </div>

                      {/* 1. Company Name */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Company Name</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center justify-between gap-2 min-h-[38px]">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <span className={`text-sm truncate font-medium select-text ${companyName !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {companyName}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {companyName !== '—' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenChange(false);
                                  const targetCompanyId = deal.companyId || deal.company?.id || deal.clientInfo?.companyId || availableCompanies.find(c => c.name && companyName && c.name.toLowerCase() === companyName.toLowerCase())?.id;
                                  if (targetCompanyId) {
                                    navigate(`/crm/clients?tab=companies&companyId=${targetCompanyId}`);
                                  } else {
                                    navigate('/crm/clients?tab=companies');
                                  }
                                }}
                                className="focus:outline-none transition-transform active:scale-95 group/badge"
                                title="View Company Details"
                              >
                                <Badge
                                  variant="outline"
                                  className="text-[10px] capitalize font-semibold px-2 py-0.5 rounded-full shrink-0 cursor-pointer inline-flex items-center gap-1 transition-all bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 group-hover/badge:bg-emerald-500/20 group-hover/badge:border-emerald-500/40"
                                >
                                  <span>Company</span>
                                  <ExternalLink className="w-2.5 h-2.5 opacity-70 group-hover/badge:opacity-100 transition-opacity" />
                                </Badge>
                              </button>
                            )}
                            <Popover
                              open={companyPickerOpen}
                              onOpenChange={(isOpen) => {
                                setCompanyPickerOpen(isOpen);
                                if (isOpen) setCompanySearch('');
                              }}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={`p-1.5 rounded-md transition-colors shrink-0 ${
                                    companyPickerOpen
                                      ? 'text-workspace-accent bg-workspace-accent/15'
                                      : 'text-muted-foreground hover:text-workspace-accent hover:bg-workspace-accent/10'
                                  }`}
                                  title="Switch Company"
                                >
                                  <ArrowLeftRight className="w-3.5 h-3.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="end"
                                sideOffset={8}
                                className="z-[60] w-[340px] p-0 shadow-2xl border-border bg-card rounded-xl overflow-hidden"
                              >
                                <div className="px-3.5 py-2.5 border-b border-border/60 bg-muted/20 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-3.5 h-3.5 text-workspace-accent" />
                                    <span className="text-xs font-semibold text-foreground">Switch Company</span>
                                  </div>
                                  <span className="text-[11px] text-muted-foreground font-medium">
                                    {availableCompanies.length} total
                                  </span>
                                </div>

                                <div className="p-3 space-y-2">
                                  <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input
                                      autoFocus
                                      placeholder="Search companies..."
                                      value={companySearch}
                                      onChange={e => setCompanySearch(e.target.value)}
                                      className="pl-8 pr-7 h-8 text-xs bg-background border-border"
                                    />
                                    {companySearch && (
                                      <button
                                        type="button"
                                        onClick={() => setCompanySearch('')}
                                        className="absolute right-2 top-2 p-0.5 text-muted-foreground hover:text-foreground rounded"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>

                                  <ScrollArea className="h-[210px] mt-1 pr-1">
                                    <div className="space-y-1">
                                      {/* Option to clear company */}
                                      <div
                                        onClick={() => handleCompanySelect(null)}
                                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                                          !deal.companyId && !deal.clientInfo?.companyName
                                            ? 'bg-workspace-accent/10 border border-workspace-accent/30 text-foreground'
                                            : 'hover:bg-muted/60 border border-transparent text-muted-foreground'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2.5">
                                          <div className="w-6 h-6 rounded-md bg-muted/50 flex items-center justify-center shrink-0">
                                            <X className="w-3 h-3 text-muted-foreground" />
                                          </div>
                                          <span className="text-xs font-medium">None (No company)</span>
                                        </div>
                                        {!deal.companyId && !deal.clientInfo?.companyName && (
                                          <CheckCircle2 className="w-3.5 h-3.5 text-workspace-accent shrink-0 ml-1.5" />
                                        )}
                                      </div>

                                      {filteredCompanies.length > 0 ? (
                                        filteredCompanies.map(comp => {
                                          const isSelected = deal.companyId === comp.id || deal.clientInfo?.companyName === comp.name;
                                          return (
                                            <div
                                              key={comp.id}
                                              onClick={() => handleCompanySelect(comp)}
                                              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                                                isSelected
                                                  ? 'bg-workspace-accent/10 border border-workspace-accent/30 text-foreground'
                                                  : 'hover:bg-muted/60 border border-transparent text-foreground'
                                              }`}
                                            >
                                              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                <div className="w-6 h-6 rounded-md bg-workspace-accent/10 text-workspace-accent flex items-center justify-center shrink-0">
                                                  <Building2 className="w-3 h-3" />
                                                </div>
                                                <div className="flex flex-col min-w-0 overflow-hidden">
                                                  <span className="font-medium truncate text-xs">{comp.name}</span>
                                                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground truncate">
                                                    {comp.category && <span className="truncate">{comp.category}</span>}
                                                    {comp.website && <span className="truncate shrink-0">• {comp.website}</span>}
                                                  </div>
                                                </div>
                                              </div>
                                              {isSelected && (
                                                <CheckCircle2 className="w-3.5 h-3.5 text-workspace-accent shrink-0 ml-1.5" />
                                              )}
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div className="text-center py-8 text-xs text-muted-foreground">
                                          No companies found
                                        </div>
                                      )}
                                    </div>
                                  </ScrollArea>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                      </div>

                      {/* 2. Category / Industry */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Category / Industry</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center gap-2 min-h-[38px] select-text">
                          <Tag className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className={`text-sm truncate font-medium ${category !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {category}
                          </span>
                        </div>
                      </div>

                      {/* 3. Website */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium">Website</Label>
                        <div className="px-3 py-2 rounded-lg border border-border/70 bg-card/60 flex items-center justify-between gap-2 min-h-[38px]">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <span className={`text-sm truncate font-medium select-text ${website !== '—' ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {website}
                            </span>
                          </div>
                          {website !== '—' && (
                            <a
                              href={website.startsWith('http') ? website : `https://${website}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-muted-foreground hover:text-workspace-accent p-1 rounded transition-colors shrink-0"
                              title="Open Website"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden bg-background">
                <ScrollArea className="flex-1">
                  <div className="p-8 max-w-3xl mx-auto w-full">
                    <h3 className="text-base font-semibold text-foreground mb-6">Activity Timeline</h3>
                    {user && (
                      <ActivityTimeline dealId={deal.id} currentUserId={user.id} />
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>



      {/* Won/Lost dialog */}
      {wonLostDialog && (
        <WonLostDialog
          open={!!wonLostDialog}
          onOpenChange={() => setWonLostDialog(null)}
          type={wonLostDialog}
          dealTitle={deal.title}
          onConfirm={async (reason) => {
            if (wonLostDialog === 'won') await onMarkWon(deal.id, reason);
            else await onMarkLost(deal.id, reason);
            setWonLostDialog(null);
          }}
        />
      )}
    </>
  );
};
